import {
  computed,
  onMounted,
  onUnmounted,
  shallowRef,
  toValue,
  watch,
  type MaybeRefOrGetter,
} from 'vue'
import { useEventListener } from '@vueuse/core'
import { storeToRefs } from 'pinia'
import {
  ReportEvents,
  SHAFT_TYPE_HEAD,
  SHAFT_TYPE_ID_MASK,
  type GcomeKeyDebugUpload,
} from '@rapoo/device-sdk'
import type { KeyConfig } from '@rapoo/element'
import { useDeviceStore } from '@/stores'
import { defaultReportError } from '@/composables/useErrorLogger'
import { getKeyConfigByTypeCode, KEYCODE_KEYWORDS_MAP } from '../../../utils/keyMapSettings'
import { resolveKeyboardHidFromEvent } from '../../TabMacro/utils/keycodeMaps'

/** 协议按键索引上限（触发行程参数区固定 128 键）。 */
const KEY_INDEX_MAX = 128
/** 协议「按键行程」字段范围：0-40 档，1 档 = 0.1mm。 */
const TRAVEL_STEP_MAX = 40
/** 同一按键的设备上报与系统 keydown 可能连续到达，合并为一次填充。 */
const CAPTURE_DEBOUNCE_MS = 300

type UsePhysicalKeyPressCaptureOptions = {
  /** 为 false 时关闭调试上报并停止填充（提交中 / 离开编辑页）。 */
  enabled?: MaybeRefOrGetter<boolean>
  onKeyPress: (key: KeyConfig) => void
}

/**
 * key_debug 上报标志（0xA0）与「触发行程参数区」响应撞码。
 * 与行程演示页同一套过滤，避免读参响应被当成一次按下。
 */
const isTravelBlockEcho = (raw: number[]) =>
  (raw[8] & ~SHAFT_TYPE_ID_MASK) === SHAFT_TYPE_HEAD &&
  (raw[16] & ~SHAFT_TYPE_ID_MASK) === SHAFT_TYPE_HEAD

const readTravelStep = (data: GcomeKeyDebugUpload): number | null => {
  const step = data.mmTravel
  if (!Number.isInteger(step) || step < 0 || step > TRAVEL_STEP_MAX) return null
  if (isTravelBlockEcho(data.raw)) return null
  return step
}

const readKeyIndex = (data: GcomeKeyDebugUpload): number | null => {
  const index = data.keyInfo[0]
  return Number.isInteger(index) && index >= 0 && index < KEY_INDEX_MAX ? index : null
}

/**
 * RS / SOCD 编辑时监听设备按键调试上报：物理按下即得到矩阵索引对应的 KeyConfig。
 * 开启 features.keyTravel.switchDemoMode 后才会持续上报；离开时必须关闭，避免键盘常发包。
 */
export function usePhysicalKeyPressCapture(options: UsePhysicalKeyPressCaptureOptions) {
  const { onKeyPress } = options
  const enabled = computed(() => toValue(options.enabled) ?? true)

  const deviceStore = useDeviceStore()
  const { currentDevice } = storeToRefs(deviceStore)

  const demoActive = shallowRef(false)
  const pressedIndexes = new Set<number>()
  let lastCaptureKeywords = ''
  let lastCaptureAt = 0

  const layoutKeys = computed(() => {
    const byIndex = new Map<number, KeyConfig>()
    const byKeywords = new Map<string, KeyConfig>()
    const layout = currentDevice.value?.manifest.layout
    if (!layout) return { byIndex, byKeywords }

    for (const row of layout.keys) {
      for (const layoutKey of row) {
        const config = KEYCODE_KEYWORDS_MAP.get(layoutKey.keywords)
        if (!config) continue
        const key = { ...config, key: layoutKey.key }
        byIndex.set(layoutKey.key, key)
        byKeywords.set(layoutKey.keywords, key)
      }
    }

    return { byIndex, byKeywords }
  })

  const emitPress = (key: KeyConfig) => {
    const now = Date.now()
    if (key.keywords === lastCaptureKeywords && now - lastCaptureAt < CAPTURE_DEBOUNCE_MS) return
    lastCaptureKeywords = key.keywords
    lastCaptureAt = now
    onKeyPress(key)
  }

  const onKeyDebugReport = (data: GcomeKeyDebugUpload) => {
    if (!demoActive.value || !enabled.value) return

    const step = readTravelStep(data)
    if (step === null) return

    const index = readKeyIndex(data)
    if (index == null) return

    if (step === 0) {
      pressedIndexes.delete(index)
      return
    }

    if (pressedIndexes.has(index)) return
    pressedIndexes.add(index)

    const key = layoutKeys.value.byIndex.get(index)
    if (!key) return
    emitPress(key)
  }

  const handleWindowKeydown = (event: KeyboardEvent) => {
    if (!enabled.value || event.repeat) return

    const target = event.target
    if (target instanceof HTMLElement) {
      if (
        target.closest(
          'input, textarea, button, [role="slider"], [role="switch"], [contenteditable="true"]',
        )
      ) {
        return
      }
    }

    const hid = resolveKeyboardHidFromEvent(event)
    if (hid == null) return
    const meta = getKeyConfigByTypeCode(1, hid)
    if (!meta) return
    const key = layoutKeys.value.byKeywords.get(meta.keywords)
    if (!key) return

    event.preventDefault()
    emitPress(key)
  }

  let unbind: (() => void) | null = null
  let demoDevice: typeof currentDevice.value
  let isMounted = false

  const detach = () => {
    unbind?.()
    unbind = null
    demoActive.value = false
    pressedIndexes.clear()
  }

  const startCapture = async () => {
    if (!isMounted || demoActive.value || demoDevice) return
    const device = currentDevice.value
    const feature = device?.features.keyTravel
    if (!feature) return

    try {
      await feature.switchDemoMode(true)
    } catch (error) {
      console.error('Failed to switch key travel demo mode', error)
      defaultReportError(`Failed to switch key travel demo mode: ${JSON.stringify(error)}`)
      return
    }

    demoDevice = device
    if (currentDevice.value !== device || !enabled.value || !isMounted) {
      demoDevice = null
      await feature.switchDemoMode(false).catch(() => undefined)
      return
    }

    device.on(ReportEvents.KeyCalibUpload, onKeyDebugReport)
    unbind = () => device.off(ReportEvents.KeyCalibUpload, onKeyDebugReport)
    demoActive.value = true
  }

  const stopCapture = async () => {
    const device = demoDevice
    detach()
    demoDevice = null
    if (!device) return

    const feature = device.features.keyTravel
    if (!feature) return
    await feature.switchDemoMode(false).catch(() => undefined)
  }

  let queue = Promise.resolve()
  const sync = (on: boolean) => {
    queue = queue.then(() => (on ? startCapture() : stopCapture())).catch(() => undefined)
  }

  useEventListener(window, 'keydown', handleWindowKeydown, { capture: true })

  onMounted(() => {
    isMounted = true
    if (enabled.value) sync(true)
  })

  watch(enabled, (on) => sync(on))

  watch(
    () => currentDevice.value?.features.keyTravel,
    () => {
      detach()
      sync(false)
      if (enabled.value && isMounted) sync(true)
    },
  )

  onUnmounted(() => {
    isMounted = false
    detach()
    sync(false)
  })
}
